import { toast } from "sonner";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Search,
  ShieldCheck,
  HeartHandshake,
  Filter,
  RotateCcw,
  Power,
  PowerOff,
  X,
} from "lucide-react";

// ── Filter option constants ───────────────────────────────────────
// Kept as sentinel non-empty strings ("__any") because Radix
// <SelectItem/> does not accept "" as a value. We treat "__any" as
// "no filter" client-side and strip it before sending to the API.
const ANY = "__any";

const STATUS_OPTIONS = [
  { value: ANY, label: "Any status" },
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
  { value: "expired", label: "Expired" },
  { value: "pending_payment", label: "Pending payment" },
  { value: "trial_ended", label: "Trial ended" },
];

const GAME_TYPE_OPTIONS = [
  { value: ANY, label: "Any game" },
  { value: "spin_wheel", label: "Spin wheel" },
  { value: "scratch_card", label: "Scratch card" },
  { value: "static_offer", label: "Static offer" },
];

const TRISTATE_OPTIONS = [
  { value: ANY, label: "Any" },
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

const gameTypeLabel = (g) =>
  ({
    spin_wheel: "Spin",
    scratch_card: "Scratch",
    static_offer: "Static",
  }[g] || g || "—");

const StatusBadge = ({ c }) => {
  const s = (c.status || "").toLowerCase();
  const tone =
    s === "active"
      ? "bg-emerald-500/10 text-emerald-700 border-emerald-500/30 dark:text-emerald-300"
      : s === "paused"
      ? "bg-amber-500/10 text-amber-700 border-amber-500/30 dark:text-amber-300"
      : s === "expired"
      ? "bg-muted text-muted-foreground border-border"
      : "bg-secondary text-foreground border-border";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium capitalize ${tone}`}>
      {s || "—"}
      {c.admin_deactivated && <span className="uppercase text-[9px] font-semibold opacity-80">(admin)</span>}
    </span>
  );
};

export default function AdminCampaignsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  // ── Filter state ────────────────────────────────────────────────
  const [q, setQ] = useState("");
  const [city, setCity] = useState(ANY);
  const [vendorId, setVendorId] = useState(ANY);
  const [status, setStatus] = useState(ANY);
  const [gameType, setGameType] = useState(ANY);
  const [verified, setVerified] = useState(ANY);
  const [trusted, setTrusted] = useState(ANY);
  const [listed, setListed] = useState(ANY);
  const [adminOff, setAdminOff] = useState(ANY);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);

  // ── Deactivate confirmation dialog ─────────────────────────────
  const [confirmTarget, setConfirmTarget] = useState(null); // campaign obj
  const [reason, setReason] = useState("");
  const [confirmBusy, setConfirmBusy] = useState(false);

  // Debounce ref so free-text search doesn't fire on every keystroke.
  const debounceRef = useRef(null);

  const buildParams = useCallback(() => {
    const p = {};
    const trimmed = q.trim();
    if (trimmed) p.q = trimmed;
    if (city !== ANY) p.city = city;
    if (vendorId !== ANY) p.vendor_id = vendorId;
    if (status !== ANY) p.status = status;
    if (gameType !== ANY) p.game_type = gameType;
    if (verified !== ANY) p.verified = verified === "true";
    if (trusted !== ANY) p.trusted = trusted === "true";
    if (listed !== ANY) p.listed_publicly = listed === "true";
    if (adminOff !== ANY) p.admin_deactivated = adminOff === "true";
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    return p;
  }, [q, city, vendorId, status, gameType, verified, trusted, listed, adminOff, dateFrom, dateTo]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.adminAllCampaigns(buildParams());
      setItems(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load campaigns");
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  // Initial + on-filter-change fetch (debounced 300ms for text/date;
  // dropdowns fire immediately since state settles atomically).
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchList, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [fetchList]);

  // Derive filter option lists from the currently loaded set so we
  // don't need a separate endpoint. Cities/vendors that don't match
  // the CURRENT filter set won't appear — that's the expected UX for
  // a compound filter (each dropdown narrows the possible next choices).
  const cityOptions = useMemo(() => {
    const set = new Set();
    items.forEach((c) => {
      const v = (c.effective_city || "").trim();
      if (v) set.add(v);
    });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [items]);
  const vendorOptions = useMemo(() => {
    const m = new Map();
    items.forEach((c) => {
      if (c.vendor_id && c.vendor_business) m.set(c.vendor_id, c.vendor_business);
    });
    return [...m.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [items]);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (q.trim()) n += 1;
    if (city !== ANY) n += 1;
    if (vendorId !== ANY) n += 1;
    if (status !== ANY) n += 1;
    if (gameType !== ANY) n += 1;
    if (verified !== ANY) n += 1;
    if (trusted !== ANY) n += 1;
    if (listed !== ANY) n += 1;
    if (adminOff !== ANY) n += 1;
    if (dateFrom) n += 1;
    if (dateTo) n += 1;
    return n;
  }, [q, city, vendorId, status, gameType, verified, trusted, listed, adminOff, dateFrom, dateTo]);

  const resetFilters = () => {
    setQ("");
    setCity(ANY);
    setVendorId(ANY);
    setStatus(ANY);
    setGameType(ANY);
    setVerified(ANY);
    setTrusted(ANY);
    setListed(ANY);
    setAdminOff(ANY);
    setDateFrom("");
    setDateTo("");
  };

  // ── Badge toggles ──────────────────────────────────────────────
  // When the current admin is a franchise/franchise_staff, route
  // through the /franchise/campaigns/{id}/verified endpoint (which
  // enforces city scope server-side). Admin/Super Admin continue
  // to hit /admin/campaigns/{id}/verified. Same UX either way.
  const { admin: currentAdmin } = useAdminAuth();
  const isFranchiseTier = ["franchise", "franchise_staff"].includes(currentAdmin?.role);

  const toggleVerified = async (c, next) => {
    setBusyId(c.id);
    try {
      const r = isFranchiseTier
        ? await api.franchiseSetVerified(c.id, next)
        : await api.adminSetVerified(c.id, next);
      setItems((arr) => arr.map((x) => (x.id === c.id ? { ...x, is_verified: !!r.data.is_verified } : x)));
      toast.success(next ? "Verified badge granted" : "Verified badge removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update Verified status");
    } finally {
      setBusyId(null);
    }
  };
  const toggleTrusted = async (c, next) => {
    setBusyId(c.id);
    try {
      const r = isFranchiseTier
        ? await api.franchiseSetTrusted(c.id, next)
        : await api.adminSetTrusted(c.id, next);
      setItems((arr) => arr.map((x) => (x.id === c.id ? { ...x, is_trusted: !!r.data.is_trusted } : x)));
      toast.success(next ? "Trusted badge granted" : "Trusted badge removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update Trusted status");
    } finally {
      setBusyId(null);
    }
  };

  // ── Deactivate / Reactivate ────────────────────────────────────
  const openConfirm = (c) => {
    setConfirmTarget(c);
    setReason("");
  };
  const closeConfirm = () => {
    if (confirmBusy) return;
    setConfirmTarget(null);
    setReason("");
  };
  const doDeactivate = async () => {
    if (!confirmTarget) return;
    setConfirmBusy(true);
    try {
      const r = await api.adminDeactivateCampaign(confirmTarget.id, reason.trim());
      const updated = r?.data?.campaign;
      if (updated) {
        setItems((arr) => arr.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
      }
      toast.success(`Campaign "${confirmTarget.name}" deactivated`);
      setConfirmTarget(null);
      setReason("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to deactivate campaign");
    } finally {
      setConfirmBusy(false);
    }
  };
  const doReactivate = async (c) => {
    setBusyId(c.id);
    try {
      const r = await api.adminReactivateCampaign(c.id);
      const updated = r?.data?.campaign;
      if (updated) {
        setItems((arr) => arr.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
      }
      toast.success(`Campaign "${c.name}" reactivated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reactivate campaign");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4 md:space-y-6" data-testid="admin-campaigns-page">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-semibold">All campaigns</h1>
        <p className="text-muted-foreground text-sm">
          Filter, review, verify or deactivate any campaign across every vendor.
        </p>
      </div>

      {/* ── Filter bar ─────────────────────────────────────────── */}
      <Card className="p-3 md:p-4">
        <div className="flex flex-wrap items-center gap-2 md:gap-3">
          <div className="relative flex-1 min-w-[180px] md:min-w-[280px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Search campaign name"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="admin-campaigns-search"
            />
          </div>

          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-[140px]" data-testid="admin-filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={gameType} onValueChange={setGameType}>
            <SelectTrigger className="w-[130px]" data-testid="admin-filter-game">
              <SelectValue placeholder="Game" />
            </SelectTrigger>
            <SelectContent>
              {GAME_TYPE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            type="button"
            variant="outline"
            onClick={() => setFiltersOpen((v) => !v)}
            data-testid="admin-filters-toggle"
            className="gap-2"
          >
            <Filter className="h-4 w-4" />
            <span className="hidden sm:inline">More filters</span>
            <span className="sm:hidden">Filters</span>
            {activeFilterCount > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 min-w-[20px] px-1.5 tabular-nums">
                {activeFilterCount}
              </Badge>
            )}
          </Button>

          {activeFilterCount > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={resetFilters}
              data-testid="admin-filters-reset"
              className="gap-1 text-muted-foreground"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Reset
            </Button>
          )}
        </div>

        {filtersOpen && (
          <div className="mt-3 md:mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="admin-filters-panel">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground">City</label>
              <Select value={city} onValueChange={setCity}>
                <SelectTrigger data-testid="admin-filter-city">
                  <SelectValue placeholder="Any city" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY}>Any city</SelectItem>
                  {cityOptions.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Vendor</label>
              <Select value={vendorId} onValueChange={setVendorId}>
                <SelectTrigger data-testid="admin-filter-vendor">
                  <SelectValue placeholder="Any vendor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY}>Any vendor</SelectItem>
                  {vendorOptions.map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Verified</label>
              <Select value={verified} onValueChange={setVerified}>
                <SelectTrigger data-testid="admin-filter-verified">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TRISTATE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Trusted</label>
              <Select value={trusted} onValueChange={setTrusted}>
                <SelectTrigger data-testid="admin-filter-trusted">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TRISTATE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Listed publicly</label>
              <Select value={listed} onValueChange={setListed}>
                <SelectTrigger data-testid="admin-filter-listed">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TRISTATE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Admin deactivated</label>
              <Select value={adminOff} onValueChange={setAdminOff}>
                <SelectTrigger data-testid="admin-filter-admin-off">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TRISTATE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Created from</label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid="admin-filter-date-from"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Created to</label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                data-testid="admin-filter-date-to"
              />
            </div>
          </div>
        )}
      </Card>

      {/* ── Results ──────────────────────────────────────────── */}
      {loading ? (
        <Skeleton className="h-60 w-full" />
      ) : (
        <>
          {/* Desktop / tablet — data-dense table. Mobile falls back
              to a stacked card list below so 8+ columns don't force a
              horizontal scrollbar on 360-430px screens. */}
          <Card className="overflow-hidden hidden lg:block">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-campaigns-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground bg-secondary/40">
                    <th className="py-3 px-4">Campaign</th>
                    <th className="py-3 px-4">Vendor</th>
                    <th className="py-3 px-4">City</th>
                    <th className="py-3 px-4">Game</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4">Verified</th>
                    <th className="py-3 px-4">Trusted</th>
                    <th className="py-3 px-4">Created</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((c) => (
                    <tr key={c.id} className="border-t border-border" data-testid={`admin-campaigns-row-${c.id}`}>
                      <td className="py-3 px-4 font-medium">{c.name}</td>
                      <td className="py-3 px-4 text-muted-foreground">{c.vendor_business || "—"}</td>
                      <td className="py-3 px-4 text-muted-foreground">{c.effective_city || "—"}</td>
                      <td className="py-3 px-4 text-muted-foreground">{gameTypeLabel(c.game_type)}</td>
                      <td className="py-3 px-4"><StatusBadge c={c} /></td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={!!c.is_verified}
                            onCheckedChange={(v) => toggleVerified(c, !!v)}
                            disabled={busyId === c.id}
                            data-testid={`admin-verified-toggle-${c.id}`}
                            aria-label={`Toggle Verified for ${c.name}`}
                          />
                          {c.is_verified && <ShieldCheck className="h-4 w-4 text-[#F5A800]" />}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={!!c.is_trusted}
                            onCheckedChange={(v) => toggleTrusted(c, !!v)}
                            disabled={busyId === c.id}
                            data-testid={`admin-trusted-toggle-${c.id}`}
                            aria-label={`Toggle Trusted for ${c.name}`}
                          />
                          {c.is_trusted && <HeartHandshake className="h-4 w-4 text-[#0A0A0A] dark:text-white" />}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-muted-foreground whitespace-nowrap">
                        {c.created_at ? new Date(c.created_at).toLocaleDateString() : ""}
                      </td>
                      <td className="py-3 px-4 text-right">
                        {c.admin_deactivated ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => doReactivate(c)}
                            disabled={busyId === c.id}
                            data-testid={`admin-reactivate-${c.id}`}
                            className="gap-1"
                          >
                            <Power className="h-3.5 w-3.5" /> Reactivate
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openConfirm(c)}
                            disabled={busyId === c.id}
                            data-testid={`admin-deactivate-${c.id}`}
                            className="gap-1 text-destructive hover:text-destructive"
                          >
                            <PowerOff className="h-3.5 w-3.5" /> Deactivate
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {items.length === 0 && (
                    <tr><td className="py-10 text-center text-muted-foreground" colSpan="9">No campaigns match these filters.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Mobile / tablet — stacked card list. All fields preserved
              but re-flowed into label/value pairs so nothing clips on
              narrow screens. Full Deactivate/Reactivate row included. */}
          <div className="lg:hidden space-y-3" data-testid="admin-campaigns-mobile-list">
            {items.length === 0 ? (
              <Card className="p-10 text-center text-muted-foreground text-sm">
                No campaigns match these filters.
              </Card>
            ) : (
              items.map((c) => (
                <Card key={c.id} className="p-4" data-testid={`admin-campaigns-mobile-card-${c.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold truncate">{c.name}</p>
                      <p className="text-xs text-muted-foreground truncate mt-0.5">
                        {c.vendor_business || "—"}
                      </p>
                    </div>
                    <StatusBadge c={c} />
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2 text-[11px] text-muted-foreground">
                    <div className="min-w-0">
                      <div className="uppercase tracking-wider">City</div>
                      <div className="text-foreground truncate">{c.effective_city || "—"}</div>
                    </div>
                    <div>
                      <div className="uppercase tracking-wider">Game</div>
                      <div className="text-foreground capitalize">{gameTypeLabel(c.game_type)}</div>
                    </div>
                    <div>
                      <div className="uppercase tracking-wider">Created</div>
                      <div className="text-foreground whitespace-nowrap">
                        {c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
                    <span className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                      <ShieldCheck className="h-3.5 w-3.5" /> Verified
                    </span>
                    <Switch
                      checked={!!c.is_verified}
                      onCheckedChange={(v) => toggleVerified(c, !!v)}
                      disabled={busyId === c.id}
                      data-testid={`admin-verified-toggle-mobile-${c.id}`}
                    />
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                      <HeartHandshake className="h-3.5 w-3.5" /> Trusted
                    </span>
                    <Switch
                      checked={!!c.is_trusted}
                      onCheckedChange={(v) => toggleTrusted(c, !!v)}
                      disabled={busyId === c.id}
                      data-testid={`admin-trusted-toggle-mobile-${c.id}`}
                    />
                  </div>
                  <div className="mt-3 pt-3 border-t border-border">
                    {c.admin_deactivated ? (
                      <Button
                        variant="outline"
                        onClick={() => doReactivate(c)}
                        disabled={busyId === c.id}
                        data-testid={`admin-reactivate-mobile-${c.id}`}
                        className="w-full gap-2"
                      >
                        <Power className="h-4 w-4" /> Reactivate campaign
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        onClick={() => openConfirm(c)}
                        disabled={busyId === c.id}
                        data-testid={`admin-deactivate-mobile-${c.id}`}
                        className="w-full gap-2 text-destructive hover:text-destructive"
                      >
                        <PowerOff className="h-4 w-4" /> Deactivate campaign
                      </Button>
                    )}
                  </div>
                </Card>
              ))
            )}
          </div>
        </>
      )}

      {/* ── Deactivate confirmation ──────────────────────────── */}
      <AlertDialog open={!!confirmTarget} onOpenChange={(open) => !open && closeConfirm()}>
        <AlertDialogContent data-testid="admin-deactivate-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate this campaign?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium text-foreground">{confirmTarget?.name}</span> will be paused
              and removed from the public Explore feed. Direct /play links will show a &ldquo;not active&rdquo;
              notice. You can reactivate it any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1.5">
            <label className="text-xs uppercase tracking-wider text-muted-foreground">
              Reason (optional, kept in audit log)
            </label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Reported for misleading claim"
              maxLength={500}
              rows={3}
              data-testid="admin-deactivate-reason"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={confirmBusy} data-testid="admin-deactivate-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); doDeactivate(); }}
              disabled={confirmBusy}
              data-testid="admin-deactivate-confirm"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {confirmBusy ? "Deactivating…" : "Yes, deactivate"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
